import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Save, Trash2, Plus, Star, Eye, EyeOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const slugify = (str) =>
  String(str)
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '_')
    .replace(/^-+|-+$/g, '');

// ====== Package row ======
const PackageRow = ({ pkg, onSave, onDelete }) => {
  const [draft, setDraft] = useState(pkg);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDraft(pkg);
    setDirty(false);
  }, [pkg]);

  const set = (field, value) => {
    setDraft((d) => ({ ...d, [field]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    setBusy(true);
    // regular_price sent as null when the field is empty — clears the founding-price strike-through everywhere
    const rawRegular = draft.regular_price;
    const regularPricePayload =
      rawRegular === '' || rawRegular === null || rawRegular === undefined
        ? null
        : Number(rawRegular);
    await onSave(pkg.id, {
      name: draft.name,
      price: Number(draft.price),
      regular_price: regularPricePayload,
      description: draft.description ?? '',
      hours: draft.hours == null ? null : Number(draft.hours),
      is_active: !!draft.is_active,
      is_popular: !!draft.is_popular,
      sort_order: Number(draft.sort_order ?? 100),
    });
    setBusy(false);
    setDirty(false);
  };

  return (
    <Card className={`border-2 ${draft.is_active ? 'border-[#8B7355]/30' : 'border-gray-300 opacity-70'}`} data-testid={`package-row-${pkg.slug}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="bg-[#8B7355] text-white">{pkg.slug}</Badge>
            {draft.is_popular && <Badge className="bg-[#C9A961] text-white"><Star className="w-3 h-3 mr-1" />Popular</Badge>}
            {!draft.is_active && <Badge className="bg-gray-400 text-white">Hidden</Badge>}
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => set('is_active', !draft.is_active)}
              className="border-[#8B7355] text-[#8B7355]"
              data-testid={`pkg-toggle-active-${pkg.slug}`}
            >
              {draft.is_active ? <><EyeOff className="w-4 h-4 mr-1" />Hide</> : <><Eye className="w-4 h-4 mr-1" />Show</>}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDelete(pkg)}
              disabled={busy}
              className="border-red-500 text-red-600 hover:bg-red-500 hover:text-white"
              data-testid={`pkg-delete-${pkg.slug}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-[#8B7355] text-xs">Display Name</Label>
            <Input value={draft.name || ''} onChange={(e) => set('name', e.target.value)} data-testid={`pkg-name-${pkg.slug}`} />
          </div>
          <div>
            <Label className="text-[#8B7355] text-xs">Price (AUD)</Label>
            <Input type="number" min="0" step="1" value={draft.price ?? 0} onChange={(e) => set('price', e.target.value)} data-testid={`pkg-price-${pkg.slug}`} />
          </div>
          <div>
            <Label className="text-[#8B7355] text-xs">
              Regular price (strike-through)
              {draft.regular_price != null && draft.regular_price !== '' && (
                <button
                  type="button"
                  onClick={() => set('regular_price', null)}
                  className="ml-2 text-red-600 underline text-xs"
                  data-testid={`pkg-clear-regular-${pkg.slug}`}
                >
                  Clear
                </button>
              )}
            </Label>
            <Input
              type="number"
              min="0"
              step="1"
              placeholder="Leave blank to hide the discount"
              value={draft.regular_price ?? ''}
              onChange={(e) => set('regular_price', e.target.value)}
              data-testid={`pkg-regular-price-${pkg.slug}`}
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[#8B7355] text-xs">Tagline / Description</Label>
            <Textarea rows={2} value={draft.description || ''} onChange={(e) => set('description', e.target.value)} />
          </div>
          <div>
            <Label className="text-[#8B7355] text-xs">Hours included</Label>
            <Input type="number" min="0" step="0.5" value={draft.hours ?? ''} onChange={(e) => set('hours', e.target.value)} />
          </div>
          <div>
            <Label className="text-[#8B7355] text-xs">Sort order (lower = first)</Label>
            <Input type="number" value={draft.sort_order ?? 100} onChange={(e) => set('sort_order', e.target.value)} />
          </div>
          <label className="flex items-center gap-2 mt-2">
            <input type="checkbox" checked={!!draft.is_popular} onChange={(e) => set('is_popular', e.target.checked)} />
            <span className="text-[#8B7355] text-sm">Show "Popular" badge</span>
          </label>
        </div>
        <div className="mt-4 flex justify-end">
          <Button
            onClick={handleSave}
            disabled={!dirty || busy}
            className="bg-[#8B7355] hover:bg-[#6B5845] text-white"
            data-testid={`pkg-save-${pkg.slug}`}
          >
            <Save className="w-4 h-4 mr-2" />
            {busy ? 'Saving...' : (dirty ? 'Save changes' : 'Saved')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ====== Addon row ======
const AddonRow = ({ addon, packages, onSave, onDelete }) => {
  const [draft, setDraft] = useState(addon);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  // Memoised list of selectable packages (filter is potentially run many times
  // because this row re-renders on every field edit).
  const selectablePackages = useMemo(
    () => packages.filter((p) => p.is_active),
    [packages],
  );

  useEffect(() => {
    setDraft(addon);
    setDirty(false);
  }, [addon]);

  const set = (field, value) => {
    setDraft((d) => ({ ...d, [field]: value }));
    setDirty(true);
  };

  const togglePkg = (slug) => {
    const current = draft.included_in_packages || [];
    const next = current.includes(slug)
      ? current.filter((s) => s !== slug)
      : [...current, slug];
    set('included_in_packages', next);
  };

  const handleSave = async () => {
    setBusy(true);
    await onSave(addon.id, {
      name: draft.name,
      price: Number(draft.price),
      description: draft.description ?? '',
      is_active: !!draft.is_active,
      included_in_packages: draft.included_in_packages || [],
      sort_order: Number(draft.sort_order ?? 100),
    });
    setBusy(false);
    setDirty(false);
  };

  return (
    <Card className={`border-2 ${draft.is_active ? 'border-[#8B7355]/30' : 'border-gray-300 opacity-70'}`} data-testid={`addon-row-${addon.slug}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="bg-[#C9A961] text-white">{addon.slug}</Badge>
            {!draft.is_active && <Badge className="bg-gray-400 text-white">Hidden</Badge>}
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => set('is_active', !draft.is_active)}
              className="border-[#8B7355] text-[#8B7355]"
              data-testid={`addon-toggle-active-${addon.slug}`}
            >
              {draft.is_active ? <><EyeOff className="w-4 h-4 mr-1" />Hide</> : <><Eye className="w-4 h-4 mr-1" />Show</>}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDelete(addon)}
              disabled={busy}
              className="border-red-500 text-red-600 hover:bg-red-500 hover:text-white"
              data-testid={`addon-delete-${addon.slug}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-[#8B7355] text-xs">Display Name</Label>
            <Input value={draft.name || ''} onChange={(e) => set('name', e.target.value)} data-testid={`addon-name-${addon.slug}`} />
          </div>
          <div>
            <Label className="text-[#8B7355] text-xs">Price (AUD)</Label>
            <Input type="number" min="0" step="1" value={draft.price ?? 0} onChange={(e) => set('price', e.target.value)} data-testid={`addon-price-${addon.slug}`} />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[#8B7355] text-xs">Description</Label>
            <Input value={draft.description || ''} onChange={(e) => set('description', e.target.value)} />
          </div>
          <div>
            <Label className="text-[#8B7355] text-xs">Sort order</Label>
            <Input type="number" value={draft.sort_order ?? 100} onChange={(e) => set('sort_order', e.target.value)} />
          </div>
          <div>
            <Label className="text-[#8B7355] text-xs">Included by default in:</Label>
            <div className="flex gap-2 mt-1 flex-wrap">
              {selectablePackages.map((p) => {
                const selected = (draft.included_in_packages || []).includes(p.slug);
                return (
                  <button
                    type="button"
                    key={p.slug}
                    onClick={() => togglePkg(p.slug)}
                    className={`px-3 py-1 text-xs font-serif rounded-full border ${
                      selected
                        ? 'bg-[#8B7355] text-white border-[#8B7355]'
                        : 'bg-white text-[#8B7355] border-[#8B7355]/30 hover:border-[#8B7355]'
                    }`}
                    data-testid={`addon-${addon.slug}-included-in-${p.slug}`}
                  >
                    {p.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button
            onClick={handleSave}
            disabled={!dirty || busy}
            className="bg-[#8B7355] hover:bg-[#6B5845] text-white"
            data-testid={`addon-save-${addon.slug}`}
          >
            <Save className="w-4 h-4 mr-2" />
            {busy ? 'Saving...' : (dirty ? 'Save changes' : 'Saved')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ====== Create modal (used for both packages and addons) ======
const CreateForm = ({ kind, packages, onCreate, onCancel }) => {
  const selectablePackages = useMemo(
    () => packages.filter((p) => p.is_active),
    [packages],
  );

  const [draft, setDraft] = useState({
    slug: '',
    name: '',
    price: kind === 'package' ? 1000 : 100,
    description: '',
    is_active: true,
    is_popular: false,
    hours: kind === 'package' ? 2 : undefined,
    included_in_packages: [],
    sort_order: 100,
  });
  const [busy, setBusy] = useState(false);

  const set = (field, value) => setDraft((d) => ({ ...d, [field]: value }));
  const setName = (value) => {
    setDraft((d) => ({ ...d, name: value, slug: d.slug || slugify(value) }));
  };

  const handleSubmit = async () => {
    if (!draft.slug || !draft.name || draft.price == null) {
      toast.error('Slug, name, and price are required');
      return;
    }
    setBusy(true);
    const payload = {
      slug: slugify(draft.slug),
      name: draft.name,
      price: Number(draft.price),
      description: draft.description,
      is_active: !!draft.is_active,
      sort_order: Number(draft.sort_order),
    };
    if (kind === 'package') {
      payload.hours = Number(draft.hours);
      payload.is_popular = !!draft.is_popular;
      if (draft.regular_price != null && draft.regular_price !== '') {
        payload.regular_price = Number(draft.regular_price);
      }
    } else {
      payload.included_in_packages = draft.included_in_packages || [];
    }
    await onCreate(payload);
    setBusy(false);
  };

  return (
    <Card className="border-2 border-[#C9A961] bg-[#FFF8EE]" data-testid={`create-${kind}-form`}>
      <CardHeader>
        <CardTitle className="font-serif text-xl text-[#8B7355] capitalize">New {kind}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-[#8B7355] text-xs">Name</Label>
            <Input value={draft.name} onChange={(e) => setName(e.target.value)} placeholder="e.g. The Premium Experience" data-testid={`create-${kind}-name`} />
          </div>
          <div>
            <Label className="text-[#8B7355] text-xs">Slug (auto-generated, can edit)</Label>
            <Input value={draft.slug} onChange={(e) => set('slug', slugify(e.target.value))} placeholder="lowercase_with_underscores" data-testid={`create-${kind}-slug`} />
          </div>
          <div>
            <Label className="text-[#8B7355] text-xs">Price (AUD)</Label>
            <Input type="number" min="0" value={draft.price} onChange={(e) => set('price', e.target.value)} data-testid={`create-${kind}-price`} />
          </div>
          {kind === 'package' && (
            <div>
              <Label className="text-[#8B7355] text-xs">Hours included</Label>
              <Input type="number" min="0" step="0.5" value={draft.hours} onChange={(e) => set('hours', e.target.value)} />
            </div>
          )}
          {kind === 'package' && (
            <div>
              <Label className="text-[#8B7355] text-xs">Regular price (optional, for strike-through discount)</Label>
              <Input
                type="number"
                min="0"
                step="1"
                placeholder="Leave blank if no promo"
                value={draft.regular_price ?? ''}
                onChange={(e) => set('regular_price', e.target.value)}
                data-testid="create-package-regular-price"
              />
            </div>
          )}
          <div className="md:col-span-2">
            <Label className="text-[#8B7355] text-xs">Description / Tagline</Label>
            <Textarea rows={2} value={draft.description} onChange={(e) => set('description', e.target.value)} />
          </div>
          {kind === 'package' && (
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={draft.is_popular} onChange={(e) => set('is_popular', e.target.checked)} />
              <span className="text-[#8B7355] text-sm">Show "Popular" badge</span>
            </label>
          )}
          {kind === 'addon' && (
            <div className="md:col-span-2">
              <Label className="text-[#8B7355] text-xs">Included by default in:</Label>
              <div className="flex gap-2 mt-1 flex-wrap">
                {selectablePackages.map((p) => {
                  const selected = (draft.included_in_packages || []).includes(p.slug);
                  return (
                    <button
                      type="button"
                      key={p.slug}
                      onClick={() => {
                        const cur = draft.included_in_packages || [];
                        set('included_in_packages', cur.includes(p.slug) ? cur.filter(s => s !== p.slug) : [...cur, p.slug]);
                      }}
                      className={`px-3 py-1 text-xs font-serif rounded-full border ${
                        selected ? 'bg-[#8B7355] text-white border-[#8B7355]' : 'bg-white text-[#8B7355] border-[#8B7355]/30'
                      }`}
                    >
                      {p.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <Button variant="outline" onClick={onCancel} className="border-[#8B7355] text-[#8B7355]">Cancel</Button>
          <Button onClick={handleSubmit} disabled={busy} className="bg-[#8B7355] hover:bg-[#6B5845] text-white" data-testid={`create-${kind}-submit`}>
            {busy ? 'Creating...' : `Create ${kind}`}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ====== Main page ======
const AdminManageProducts = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('packages'); // 'packages' | 'addons'
  const [creating, setCreating] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pkgRes, addonRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/products/packages`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/products/addons`, { withCredentials: true }),
      ]);
      setPackages(pkgRes.data.packages || []);
      setAddons(addonRes.data.addons || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load catalog');
      if (err.response?.status === 401) navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const savePackage = async (id, updates) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/products/packages/${id}`, updates, { withCredentials: true });
      toast.success('Package saved');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };
  const deletePackage = async (pkg) => {
    if (!window.confirm(`Delete package "${pkg.name}"? This cannot be undone.`)) return; // eslint-disable-line no-alert
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/products/packages/${pkg.id}`, { withCredentials: true });
      toast.success('Package deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };
  const createPackage = async (payload) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/products/packages`, payload, { withCredentials: true });
      toast.success('Package created');
      setCreating(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    }
  };

  const saveAddon = async (id, updates) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/products/addons/${id}`, updates, { withCredentials: true });
      toast.success('Add-on saved');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };
  const deleteAddon = async (addon) => {
    if (!window.confirm(`Delete add-on "${addon.name}"? This cannot be undone.`)) return; // eslint-disable-line no-alert
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/products/addons/${addon.id}`, { withCredentials: true });
      toast.success('Add-on deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };
  const createAddon = async (payload) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/products/addons`, payload, { withCredentials: true });
      toast.success('Add-on created');
      setCreating(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F1E8] p-6 flex items-center justify-center">
        <p className="text-[#8B7355] font-serif text-xl">Loading catalog...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F1E8] py-12" data-testid="admin-manage-products">
      <div className="max-w-5xl mx-auto px-6">
        <div className="mb-8 flex items-center justify-between flex-wrap gap-3">
          <div>
            <Button variant="outline" onClick={() => navigate('/admin')} className="border-[#8B7355] text-[#8B7355] mb-3" data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-4xl font-serif text-[#8B7355] italic">Manage Packages &amp; Add-ons</h1>
            <p className="text-[#8B7355]/70">Edits go live instantly on the booking form. Pricing flows straight through to Stripe.</p>
          </div>
        </div>

        <div className="flex gap-2 mb-6">
          <button
            type="button"
            onClick={() => { setTab('packages'); setCreating(false); }}
            className={`px-5 py-2 font-serif rounded-full ${tab === 'packages' ? 'bg-[#8B7355] text-white' : 'bg-white border border-[#8B7355]/30 text-[#8B7355]'}`}
            data-testid="tab-packages"
          >
            Packages ({packages.length})
          </button>
          <button
            type="button"
            onClick={() => { setTab('addons'); setCreating(false); }}
            className={`px-5 py-2 font-serif rounded-full ${tab === 'addons' ? 'bg-[#8B7355] text-white' : 'bg-white border border-[#8B7355]/30 text-[#8B7355]'}`}
            data-testid="tab-addons"
          >
            Add-ons ({addons.length})
          </button>
          <div className="ml-auto">
            {!creating && (
              <Button
                onClick={() => setCreating(true)}
                className="bg-[#C9A961] hover:bg-[#B89551] text-white"
                data-testid="create-new-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New {tab === 'packages' ? 'Package' : 'Add-on'}
              </Button>
            )}
          </div>
        </div>

        {creating && (
          <div className="mb-6">
            <CreateForm
              kind={tab === 'packages' ? 'package' : 'addon'}
              packages={packages}
              onCreate={tab === 'packages' ? createPackage : createAddon}
              onCancel={() => setCreating(false)}
            />
          </div>
        )}

        <div className="space-y-4">
          {tab === 'packages' && packages.map((pkg) => (
            <PackageRow key={pkg.id} pkg={pkg} onSave={savePackage} onDelete={deletePackage} />
          ))}
          {tab === 'addons' && addons.map((addon) => (
            <AddonRow key={addon.id} addon={addon} packages={packages} onSave={saveAddon} onDelete={deleteAddon} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminManageProducts;
